import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <nav className="w-full px-6 md:px-10 h-16 flex items-center justify-between" aria-label="Main navigation">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="flex items-center gap-2 text-foreground-950 no-underline"
          >
            <span className="text-xl font-heading tracking-tight whitespace-nowrap">
              Kakobuy<span className="text-primary-500">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/kakobuy-spreadsheet-guide"
              className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Guides
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Categories
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-700 cursor-pointer"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        </nav>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={closeMobileMenu}
          />

          <div className="absolute top-0 right-0 w-[85vw] max-w-sm h-full bg-background-50 shadow-xl animate-slide-in-right">
            <div className="flex items-center justify-between px-6 h-16 border-b border-background-200/60">
              <span className="text-lg font-heading text-foreground-950 whitespace-nowrap">
                Kakobuy<span className="text-primary-500">Finds</span>
              </span>
              <button
                onClick={closeMobileMenu}
                className="flex items-center justify-center w-10 h-10 text-foreground-600 cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="overflow-y-auto h-[calc(100%-64px)] px-5 py-4">
              <div className="mb-6">
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-background-100 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-4-line text-lg text-foreground-600"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800">Home</span>
                </Link>
                <Link
                  to="/kakobuy-spreadsheet-guide"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-background-100 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-lg text-foreground-600"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800">Guides</span>
                </Link>
              </div>

              <div className="mb-6">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 py-3 px-3 w-full rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 rounded-lg bg-background-50/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-up-line text-lg text-background-50"></i>
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium block whitespace-nowrap">Kakobuy Sheet</span>
                    <span className="text-xs text-background-50/70 whitespace-nowrap">kaspreadsheet.com</span>
                  </div>
                </a>
              </div>

              <div className="mb-2">
                <div className="flex items-center gap-2 mb-3 px-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400 whitespace-nowrap">
                    Product Categories
                  </span>
                  <div className="flex-1 h-px bg-background-200"></div>
                </div>

                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-100 transition-colors">
                        <i className={`${cat.icon} text-base text-foreground-500 group-hover:text-accent-600 transition-colors`}></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                        {cat.name}
                      </span>
                      <i className="ri-arrow-right-up-line text-xs text-foreground-300 ml-auto group-hover:text-foreground-500 transition-colors flex-shrink-0"></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-4 mb-6 px-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-shopping-bag-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}